import { useDirectoryTableColumns } from "@/appComponents/Routes/directory/useDirectoryTableColumns";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { boxObjClassFieldIds, enumObjClassNames, posObjClassFieldIds } from "@coreapp/constants";
import {
  defaultPlanningTabularFields,
  LOCAL_STORAGE_KEY_DIRECTORY_TABULAR_COLUMNS,
  planningTabularFieldDeps,
  TABLE_INITIAL_STATE,
  TABLE_PAGINATION_DEFUALT_STATE
} from "@coreapp/CoreApp/position-management.constants";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode
} from "react";
import {
  ColumnOrderState,
  OnChangeFn,
  SortingState,
  PaginationState,
  ColumnFiltersState
} from "@tanstack/react-table";
import { cloneReactChildren, DataTable } from "@theorgchartdev/storybook";
import {
  getFieldInfoFromTempColField,
  getPageOffset,
  getRowCanExpand,
  getRowId
} from "@/Utils/Table/PosMgmtUtils";
import { QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBFieldDef";
import { GetSearchResults } from "@/Utils/Promises";
import { GetTabularFieldDefs } from "@/Utils/DataLib";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { QObjDBFieldValue } from "@coreapp/CoreApp/QObjDBFieldValue";
import { toServerFilterValue, deduplicateFilterValues } from "@coreapp/utils/FormatFieldValue";

interface DirectoryTabularState {
  columns: QObjDBFieldDef[];
  results: any[];
  isFetching: boolean;
  isSearchLoading: boolean;
  sorting: SortingState;
  columnOrder: ColumnOrderState;
  pagination: PaginationState;
  columnFilters: ColumnFiltersState;
  paginationInfo: PaginationInfo;
}

interface PaginationInfo {
  hasMore: boolean;
  limit: number;
  nextOffset: number;
  offset: number;
  totalCount: number;
}

interface QueryOptions {
  mode: "directory_tabular";
  includeDeletes: true;
  handlePositions: boolean;
  pageSize: number;
  offset: number;
  sort: {
    fieldName: string;
    order: "asc" | "desc";
    forHolder: boolean;
  } | null;
  filterState: { field: string; value: any[]; forHolder: boolean }[] | null;
}

interface LocalStorageData {
  selectedColumns: { fieldID: string; objClassName: string }[];
  columnOrder: string[];
}

interface DirectoryTabularContextType {
  state: DirectoryTabularState;
  searchCtrlRef: React.RefObject<any>;
  isFetching: boolean;
  isSearchLoading: boolean;
  projectFields: QObjDBFieldDef[];
  holderFields: QObjDBFieldDef[];
  queryOptions: QueryOptions;
  tableRef: React.RefObject<HTMLDivElement>;
  updateState: (updatedState: Partial<DirectoryTabularState>) => void;
  setColumns: (columns: QObjDBFieldDef[]) => void;
  resetColumns: () => void;
  setResults: (results: any[]) => void;
  shouldFetch: (isFetching: boolean) => void;
  callQuerySvrApi: () => void;
  onSortingChange: OnChangeFn<SortingState>;
  onColumnOrderChange: OnChangeFn<ColumnOrderState>;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
  onPaginationChange: OnChangeFn<PaginationState>;
  getLocalStorageCache: () => LocalStorageData | undefined;
  setLocalStorageCache: (update: Partial<LocalStorageData>) => void;
  fetchColumnFilterOptions: (args: {
    columnId: string;
    pageIndex?: number | null;
    pageSize?: number | null;
    searchText?: string | null;
  }) => Promise<{
    data: any[];
    hasMore: boolean;
    pageIndex: number;
    pageSize: number;
  }>;
  getIsCustomChart: () => boolean;
}

interface DirectoryTabularProviderProps {
  children: ReactNode;
}

interface DataTableProviderProps {
  children: ReactNode;
}

const DirectoryTabularContext = createContext<DirectoryTabularContextType | undefined>(undefined);

const DirectoryTabularProvider = ({ children }: DirectoryTabularProviderProps): JSX.Element => {
  const searchCtrlRef = useRef<any>(null);
  const searchQueryRef = useRef<string>(); // To store the previous search query string for comparison
  const tableRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<DirectoryTabularState>({
    columns: [],
    results: [],
    isFetching: false, // indicates if the data fetch is in progress
    isSearchLoading: false, // indicates if the search input is loading data
    sorting: [], // sorting state of the table maintained outside of the DataTable
    columnOrder: [], // column order state maintained outside of the DataTable
    columnFilters: [], // column filters state maintained outside of the DataTable
    pagination: TABLE_PAGINATION_DEFUALT_STATE, // column order state maintained outside of the DataTable
    paginationInfo: {} as PaginationInfo
  });

  const wsMgr = GetWSMgr();
  const org = wsMgr?.GetAccountName();
  const doc = GetDoc();
  const chartID = doc?.GetChartID();

  const getIsCustomChart = useCallback(() => {
    return doc ? !doc?.IsServerChart() : false;
  }, [doc]);

  const isCustomChart = useMemo(() => getIsCustomChart(), [getIsCustomChart]);

  /**
   * Generates a unique key for localStorage cache based on organization and chart ID.
   */
  const getLocalStorageCacheKey = useCallback(() => {
    if (!org || !chartID) {
      console.warn("Organization or Chart ID is not defined.");
      return undefined;
    }
    return `${org}_${chartID}`;
  }, [org, chartID]);

  const getLocalStorageItemKey = useCallback(() => {
    return `${isCustomChart ? "customChart_" : ""}${LOCAL_STORAGE_KEY_DIRECTORY_TABULAR_COLUMNS}`;
  }, [isCustomChart]);

  /**
   * Retrieves the localStorage cache for table columns.
   */
  const getLocalStorageCache: DirectoryTabularContextType["getLocalStorageCache"] =
    useCallback(() => {
      const cacheKey = getLocalStorageCacheKey();
      if (!cacheKey) {
        return;
      }

      const columns = localStorage.getItem(getLocalStorageItemKey());
      if (columns) {
        const cacheData = JSON.parse(columns);
        return cacheData[cacheKey];
      }
      return;
    }, [getLocalStorageCacheKey, getLocalStorageItemKey]);

  /**
   * Updates the localStorage cache for table columns.
   */
  const setLocalStorageCache: DirectoryTabularContextType["setLocalStorageCache"] = useCallback(
    update => {
      const cacheKey = getLocalStorageCacheKey();
      if (!cacheKey) {
        return;
      }

      let cacheData: Record<string, LocalStorageData> = {};
      const localStorageItemKey = getLocalStorageItemKey();
      const columns = localStorage.getItem(localStorageItemKey);
      if (columns) cacheData = JSON.parse(columns);
      cacheData[cacheKey] = { ...(cacheData[cacheKey] ?? {}), ...update };
      localStorage.setItem(localStorageItemKey, JSON.stringify(cacheData));
    },
    [getLocalStorageCacheKey, getLocalStorageItemKey]
  );

  /**
   * Updates the sorting state of the table.
   * Called when the user changes the sorting order in the table.
   * It updates the sorting state and sets the fetching state to true to trigger a data fetch.
   * Resets selection state and page index to default.
   * @param {function} updater - A function that takes the current sorting state and updates the sorting value in state.
   */
  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    updater => {
      const sorting = typeof updater === "function" ? updater(state.sorting) : updater;

      setState(prev => ({
        ...prev,
        sorting,
        isFetching: true,
        // Reset states to default
        pagination: {
          ...prev.pagination,
          pageIndex: TABLE_PAGINATION_DEFUALT_STATE.pageIndex
        }
      }));
    },
    [state.sorting]
  );

  /**
   * Updates the column order state of the table and also saves it to the localStorage cache.
   * @param {function} updater - A function that takes the current column order state and updates it.
   */
  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = useCallback(updater => {
    setState(prev => {
      const columnOrder = typeof updater === "function" ? updater(prev.columnOrder) : updater;

      setLocalStorageCache({ columnOrder });

      return {
        ...prev,
        columnOrder
      };
    });
  }, []);

  /**
   * Updates the column filter state of the table.
   * @param {function} updater - A function that takes the current column filter state and updates it.
   */
  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback(
    updater => {
      const columnFilters = typeof updater === "function" ? updater(state.columnFilters) : updater;

      setState(prev => ({
        ...prev,
        columnFilters,
        ...(isCustomChart ? {} : { isFetching: true }),
        // Reset states to default
        pagination: {
          ...prev.pagination,
          pageIndex: TABLE_PAGINATION_DEFUALT_STATE.pageIndex
        }
      }));
    },
    [state.columnFilters]
  );

  const onPaginationChange: OnChangeFn<PaginationState> = useCallback(
    updater => {
      const pagination = typeof updater === "function" ? updater(state.pagination) : updater;

      setState(prev => ({
        ...prev,
        pagination,
        isFetching: true
      }));
    },
    [state.pagination]
  );

  /**
   *  Sets the fetching state to true or false.
   *  Used to indicate whether data should be fetched.
   * @param {boolean} isFetching
   */
  const shouldFetch = useCallback((isFetching: boolean) => {
    setState(prev => ({
      ...prev,
      isFetching
    }));
  }, []);

  /**
   * Updates any part of the state with the provided updatedState object.
   */
  const updateState = useCallback((updatedState: Partial<DirectoryTabularState>) => {
    setState(prev => ({ ...prev, ...updatedState }));
  }, []);

  /**
   * Sets the columns state with the provided column definitions.
   * Sets the fetching state to true to indicate that data should be fetched.
   * @param {QObjDBFieldDef[]} columns - The column definitions to set.
   */
  const setColumns = useCallback((columns: QObjDBFieldDef[]) => {
    setState(prev => ({
      ...prev,
      columns,
      isFetching: true
    }));
  }, []);

  /**
   * Resets the column order state to the cached column order from localStorage.
   */
  const resetColumnOrder = useCallback(() => {
    const cachedConfig = getLocalStorageCache();
    setState(prev => ({ ...prev, columnOrder: cachedConfig?.columnOrder ?? [] }));
  }, [getLocalStorageCache]);

  /**
   *  Resets the columns to the default tabular field defs.
   *  Sets the fetching state to true to indicate that data should be fetched.
   */
  const resetColumns = useCallback(() => {
    const doc = GetDoc();
    const wsMgr = GetWSMgr();
    if (!doc || !wsMgr) {
      return;
    }

    const objDb = doc.GetObjDB();
    if (!objDb) {
      return;
    }

    const columnDefs: QObjDBFieldDef[] = [];
    const allowedDefs = GetTabularFieldDefs();
    const allowedPosColumnNamesSet = new Set(
      allowedDefs.Position.map((field: QObjDBFieldDef) => field.GetFieldName())
    );
    const allowedBoxColumnNamesSet = new Set(
      allowedDefs.Box.map((field: QObjDBFieldDef) => field.GetFieldName())
    );

    const addFieldDefs = (fieldDefs: { fieldID: string; objClassName: string }[]) => {
      for (const field of fieldDefs) {
        const { fieldID, objClassName } = field;
        const objClass = objDb.GetClassByName(objClassName);
        const fieldDef = objClass?.GetFieldDefByID(fieldID);

        if (!fieldDef) {
          continue;
        }

        const fieldName = fieldDef.GetFieldName();

        if (
          (objClassName === enumObjClassNames.position &&
            allowedPosColumnNamesSet.has(fieldName)) ||
          (objClassName === enumObjClassNames.box && allowedBoxColumnNamesSet.has(fieldName))
        ) {
          columnDefs.push(fieldDef);
        }
      }
    };

    // Read localStorage for cached column config
    const cachedConfig = getLocalStorageCache();
    addFieldDefs(cachedConfig?.selectedColumns ?? []);

    if (columnDefs.length === 0) {
      addFieldDefs(defaultPlanningTabularFields);
    }

    setState(prev => ({
      ...prev,
      columns: columnDefs,
      isFetching: true
    }));
  }, []);

  /**
   * Checks if the search query string has changed compared to the previous one.
   * @returns {[string, boolean]} - Returns the new query string and a boolean indicating if it has changed.
   */
  const searchQueryChanged = (): [string, boolean] => {
    const newQueryString = searchCtrlRef.current?.getQueryString();
    const oldQueryString = searchQueryRef.current;
    const changed = newQueryString !== oldQueryString;
    return [newQueryString, changed];
  };

  const processIgnoredFilters = useCallback(
    (ignoredFilters: { field: string; value: any[]; forHolder: boolean }[]) => {
      return ignoredFilters
        ? state.columnFilters.map(f => {
            const { fieldName, fieldClassName } = getFieldInfoFromTempColField(f.id);
            const ignoredValues = ignoredFilters.filter(
              item =>
                item.field === fieldName &&
                (fieldClassName === enumObjClassNames.box ? item.forHolder : true)
            );
            return {
              ...f,
              value: ignoredValues.length
                ? (f.value as any[]).filter(
                    v => !ignoredValues.some(ignored => ignored.value.includes(v))
                  )
                : f.value
            };
          })
        : state.columnFilters;
    },
    [state.columnFilters]
  );

  const computeResultsForCustomChart = (
    dbObjs: QObjDBObject[],
    state: DirectoryTabularState
  ): DirectoryTabularState => {
    const positionMap: Record<string, any> = {};
    for (const dbObj of dbObjs) {
      const positionId = dbObj.SmartGetFieldValue(posObjClassFieldIds.POSITION_ID) as string;
      if (positionId) {
        const subRows = (dbObj.GetHolderList() ?? []).map(holder => {
          const rowObj = holder.fFieldValues.reduce(
            (acc: Record<string, any>, fieldValue: QObjDBFieldValue) => {
              const fDef = fieldValue.GetFieldDef();
              const fName = fDef.GetFieldName();
              const fValue = fieldValue.GetValue();
              const fId = fDef.GetFieldID();
              acc[fName] = fValue;
              acc["dataType"] = enumObjClassNames.box;
              if (fId === boxObjClassFieldIds.EMPLOYEE_ID) {
                acc._id = fValue ?? "";
              }

              return acc;
            },
            {}
          );
          return rowObj;
        });

        const parentPosDetails = dbObj
          .GetParent()
          ?.fFieldValues.reduce((acc: Record<string, any>, fieldValue: QObjDBFieldValue) => {
            const fDef = fieldValue.GetFieldDef();
            const fName = fDef.GetFieldName();
            const fValue = fieldValue.GetValue();
            acc[fName] = fValue;
            return acc;
          }, {});

        const posDetails = dbObj.fFieldValues.reduce(
          (acc: Record<string, any>, fieldValue: QObjDBFieldValue) => {
            const fDef = fieldValue.GetFieldDef();
            const fName = fDef.GetFieldName();
            const fValue = fieldValue.GetValue();
            const fId = fDef.GetFieldID();
            acc[fName] = fValue;
            acc.dataType = enumObjClassNames.position;
            if (fId === posObjClassFieldIds.POSITION_ID) {
              acc._id = fValue ?? "";
            }
            if (subRows.length > 0) {
              acc.subRows = subRows;
            }
            acc.__PARENT__ = parentPosDetails ? [parentPosDetails] : [];

            return acc;
          },
          {}
        );

        positionMap[positionId] = posDetails;
      }
    }

    const newResults = Object.values(positionMap);

    return {
      ...state,
      results: newResults,
      isFetching: false
    };
  };

  /**
   * Sets the results state with the fetched results.
   * Resets the fetching states to false after setting the results.
   * Resets column filters, sorting, selection and pagination states to default if the search query has changed.
   * Updates column filters if there are any ignored filters in the results info.
   * @param {any[]} results - The results fetched from the server or search.
   */
  const setResults = useCallback(
    (sResults: any) => {
      setState(prev => {
        const isCustomChart = getIsCustomChart();
        if (isCustomChart) {
          const data = computeResultsForCustomChart(sResults ?? {}, prev);
          return {
            ...prev,
            ...data
          };
        }

        // Normal ops
        const { results = [], info } = sResults ?? {};

        const paginationInfo = info?.pagination ?? {};

        const columnFilters = processIgnoredFilters(info?.ignoredFilters);

        // Determine if search query string has changed
        const [newQueryString, queryChanged] = searchQueryChanged();
        searchQueryRef.current = newQueryString;

        const resetedState: Partial<typeof state> = {
          sorting: [],
          pagination: TABLE_PAGINATION_DEFUALT_STATE,
          columnFilters: []
        };

        const posObjClass = doc?.GetObjDB()?.GetClassByName(enumObjClassNames.position);
        const posFieldDef = posObjClass?.GetFieldDefByID(posObjClassFieldIds.POSITION_ID);
        const posIndexKey = posFieldDef?.GetFieldName();

        if (!posIndexKey) {
          return prev;
        }

        const positionMap: Record<string, any> = {};
        for (const result of results) {
          const positionId = result[posIndexKey];
          if (positionId) {
            const { __HOLDERS__, ...rest } = result;
            let boxIndexKey: string | undefined;
            if (__HOLDERS__ && __HOLDERS__.length > 0) {
              const boxObjClass = doc?.GetObjDB()?.GetClassByName(enumObjClassNames.box);
              const boxFieldDef = boxObjClass?.GetFieldDefByID(boxObjClassFieldIds.EMPLOYEE_ID);
              boxIndexKey = boxFieldDef?.GetFieldName();
            }
            const subRows = (__HOLDERS__ ?? []).map((holder: { [x: string]: any }) => ({
              ...holder,
              _id: holder[boxIndexKey ?? ""],
              dataType: enumObjClassNames.box
            }));

            const posDetails = {
              ...rest,
              _id: positionId,
              dataType: enumObjClassNames.position,
              ...(subRows.length > 0 ? { subRows } : {})
            };
            positionMap[positionId] = posDetails;
          }
        }

        const newResults = Object.values(positionMap);

        // Reset states if search query has changed
        return {
          ...prev,
          results: newResults,
          isFetching: false,
          paginationInfo,
          columnFilters,
          ...(queryChanged ? resetedState : {})
        };
      });
    },
    [processIgnoredFilters, getIsCustomChart]
  );

  /**
   * Generates the project fields(field defs) based on the current columns and their dependencies.
   * @returns {QObjDBFieldDef[]} array of field defs created from columns' state.
   */
  const projectFields = useMemo((): Record<string, QObjDBFieldDef[]> => {
    const doc = GetDoc();
    const objDb = doc?.GetObjDB();

    const fields: Record<string, Record<string, QObjDBFieldDef>> = {};
    const addField = (fieldDef: QObjDBFieldDef) => {
      const fieldID = fieldDef.GetFieldID();
      const fieldObjClassName = fieldDef.GetFieldClassName();
      if (fieldObjClassName) {
        fields[fieldObjClassName] ||= {};
        fields[fieldObjClassName][fieldID] ||= fieldDef;
      }
    };
    for (const fieldDef of state.columns) {
      if (!fieldDef) {
        continue;
      }

      const fieldID = fieldDef.GetFieldID();
      const dependencies =
        planningTabularFieldDeps[fieldID as keyof typeof planningTabularFieldDeps];

      addField(fieldDef);

      if (dependencies && dependencies.length > 0) {
        for (const { fieldID, objClassName } of dependencies) {
          const objClass = objDb?.GetClassByName(objClassName);
          const depFieldDef = objClass?.GetFieldDefByID(fieldID);
          if (!depFieldDef) {
            continue;
          }

          addField(depFieldDef);
        }
      }
    }

    const returnFields: Record<string, QObjDBFieldDef[]> = {
      [enumObjClassNames.position]: [],
      [enumObjClassNames.box]: []
    };
    for (const [objClassName, fieldIdDefMap] of Object.entries(fields)) {
      returnFields[objClassName] = Object.values(fieldIdDefMap);
    }

    // planID 0 is valid (first plan on a fresh org) — test != null, not truthiness.
    const isPlan = GetWSMgr()?.GetPlanMgr()?.GetPlanID() != null;

    if (isPlan) {
      // Add dependent field defs like _metrics_* when needed for proper projection
      for (const objClassName of [enumObjClassNames.position, enumObjClassNames.box]) {
        returnFields[objClassName] = returnFields[objClassName].flatMap(f => {
          const planDef = f.GetPlanningDef?.();
          return planDef ? [f, planDef] : [f];
        });
      }
    }

    return returnFields;
  }, [state.columns]);

  /**
   * Generates the query options for the search controller based on default states.
   */
  const baseQueryOptions = useMemo((): QueryOptions => {
    const { pageIndex, pageSize } = TABLE_PAGINATION_DEFUALT_STATE;

    const options: QueryOptions = {
      mode: "directory_tabular",
      includeDeletes: true,
      handlePositions: true,
      pageSize,
      offset: getPageOffset(pageIndex, pageSize),
      sort: null,
      filterState: null
    };

    return options;
  }, []);

  /**
   * Generates the query options for needed for Query API calls outside the search controlled, based on the current states.
   */
  const queryOptions = useMemo((): QueryOptions => {
    const sortState = state.sorting;
    const paginationState = state.pagination;

    const options = { ...baseQueryOptions };

    if (sortState && sortState.length > 0) {
      const sortField = sortState[0];
      const { fieldName, fieldClassName } = getFieldInfoFromTempColField(sortField.id);
      if (fieldName) {
        options.sort = {
          fieldName,
          order: sortField.desc ? "desc" : "asc",
          forHolder: fieldClassName === enumObjClassNames.box
        };
      }
    }

    if (paginationState && paginationState.pageSize > 0) {
      options.pageSize = paginationState.pageSize;
      options.offset = getPageOffset(paginationState.pageIndex, paginationState.pageSize);
    }

    if (state.columnFilters.length > 0) {
      options.filterState = state.columnFilters.map(f => {
        const { fieldName, fieldClassName } = getFieldInfoFromTempColField(f.id);
        return {
          field: fieldName,
          value: (f.value as any[]).map(toServerFilterValue),
          forHolder: fieldClassName === enumObjClassNames.box
        };
      });
    }

    return options;
  }, [baseQueryOptions, state.sorting, state.pagination, state.columnFilters]);

  /**
   * Calls the search controller's DoSearch method(that fetches data) with latest query options and project fields.
   */
  const callQuerySvrApi = useCallback(() => {
    if (!searchCtrlRef.current) {
      return;
    }

    const queryOrCriteria = searchCtrlRef.current.getQueryString();
    searchCtrlRef.current.DoSearch({
      queryOrCriteria,
      projectFields: projectFields[enumObjClassNames.position].map(f => f.GetFieldName()),
      holderProject: projectFields[enumObjClassNames.box].map(f => f.GetFieldName()),
      queryOptions: queryOptions
    });
  }, [projectFields, queryOptions]);

  /**
   * Used to fetch column filter options for a specific column for server-side datatable.
   */
  const fetchColumnFilterOptions: DirectoryTabularContextType["fetchColumnFilterOptions"] =
    useCallback(
      async ({ columnId, pageIndex, pageSize, searchText = "" }) => {
        const currentPageIndex = pageIndex || 0;
        const currentPageSize = pageSize || 50;
        const { fieldName, fieldClassName } = getFieldInfoFromTempColField(columnId);
        const queryOrCriteria = searchCtrlRef.current.getQueryString();
        const { result } = await GetSearchResults(queryOrCriteria, {
          planID: GetWSMgr()?.GetPlanMgr()?.GetPlanID(),
          projectFields: projectFields[enumObjClassNames.position].map(f => f.GetFieldName()),
          holderProject: projectFields[enumObjClassNames.box].map(f => f.GetFieldName()),
          ...baseQueryOptions,
          pageSize: currentPageSize,
          offset: getPageOffset(currentPageIndex, currentPageSize),
          sort: {
            fieldName,
            order: "asc",
            forHolder: fieldClassName === enumObjClassNames.box
          },
          columnFilter: {
            field: fieldName,
            searchText: toServerFilterValue(searchText),
            forHolder: fieldClassName === enumObjClassNames.box
          },
          filterState: state.columnFilters.map(f => {
            const { fieldName, fieldClassName } = getFieldInfoFromTempColField(f.id);
            return {
              field: fieldName,
              value: (f.value as any[]).map(toServerFilterValue),
              forHolder: fieldClassName === enumObjClassNames.box
            };
          })
        });

        const { results, info } = result;

        const data = deduplicateFilterValues(results, fieldName);

        return {
          data,
          hasMore: info?.pagination?.hasMore === true,
          pageIndex: currentPageIndex + 1,
          pageSize: currentPageSize
        };
      },
      [state.columnFilters, projectFields]
    );

  useEffect(() => {
    resetColumns();
    resetColumnOrder();
  }, [resetColumns, resetColumnOrder]);

  // Call the query server API when the fetching state changes.
  useEffect(() => {
    if (!state.isFetching) {
      return;
    }
    callQuerySvrApi();
  }, [callQuerySvrApi, state.isFetching]);

  const contextValue = useMemo(
    (): DirectoryTabularContextType => ({
      state,
      searchCtrlRef,
      isFetching: state.isFetching,
      isSearchLoading: state.isSearchLoading,
      projectFields: projectFields[enumObjClassNames.position],
      holderFields: projectFields[enumObjClassNames.box],
      queryOptions: baseQueryOptions,
      tableRef,
      updateState,
      setColumns,
      resetColumns,
      setResults,
      shouldFetch,
      callQuerySvrApi,
      onSortingChange,
      onColumnOrderChange,
      onColumnFiltersChange,
      onPaginationChange,
      getLocalStorageCache,
      setLocalStorageCache,
      fetchColumnFilterOptions,
      getIsCustomChart
    }),
    [
      state,
      searchCtrlRef,
      projectFields,
      baseQueryOptions,
      tableRef,
      updateState,
      setColumns,
      resetColumns,
      setResults,
      shouldFetch,
      callQuerySvrApi,
      onSortingChange,
      onColumnOrderChange,
      onColumnFiltersChange,
      onPaginationChange,
      getLocalStorageCache,
      setLocalStorageCache,
      fetchColumnFilterOptions,
      getIsCustomChart
    ]
  );

  return (
    <DirectoryTabularContext.Provider value={contextValue}>
      <DataTableProvider>{cloneReactChildren(children)}</DataTableProvider>
    </DirectoryTabularContext.Provider>
  );
};

const DataTableProvider = ({ children }: DataTableProviderProps): JSX.Element => {
  const {
    state,
    onSortingChange,
    onColumnOrderChange,
    onPaginationChange,
    onColumnFiltersChange,
    getIsCustomChart
  } = useDirectoryTabularContext();
  const tableColumns = useDirectoryTableColumns();

  const isCustomChart = getIsCustomChart();

  const tableExternalState = useMemo(() => {
    if (isCustomChart) {
      return {
        columnOrder: state.columnOrder,
        columnFilters: state.columnFilters
      };
    }
    return {
      sorting: state.sorting,
      columnOrder: state.columnOrder,
      pagination: state.pagination,
      columnFilters: state.columnFilters
    };
  }, [state.sorting, state.columnOrder, state.pagination, state.columnFilters, isCustomChart]);

  const getTableRowId = useCallback(() => {
    return getRowId();
  }, [state.results]);

  const initialState = useMemo(() => {
    const objDb = GetDoc()?.GetObjDB();
    return {
      columnPinning: {
        left: Object.entries(TABLE_INITIAL_STATE.columnPinning.left).flatMap(
          ([objClassName, fIds]) => {
            const fieldIds = fIds.map(fId => {
              const fieldId = objDb?.GetMappedFieldDefId(fId, objClassName);
              return `${fieldId}_${objClassName}`;
            });
            return fieldIds;
          }
        )
      },
      pagination: TABLE_PAGINATION_DEFUALT_STATE
    };
  }, []);

  if (isCustomChart) {
    return (
      <DataTable.Provider
        data={state.results}
        columns={tableColumns}
        initialState={initialState}
        getRowCanExpand={getRowCanExpand}
        state={tableExternalState}
        onColumnOrderChange={onColumnOrderChange}
        onColumnFiltersChange={onColumnFiltersChange}
        getRowId={getTableRowId()}
        manualFiltering={false}
      >
        {children}
      </DataTable.Provider>
    );
  }

  return (
    <DataTable.Provider
      data={state.results}
      columns={tableColumns}
      initialState={initialState}
      getRowCanExpand={getRowCanExpand}
      state={tableExternalState}
      onSortingChange={onSortingChange}
      onColumnOrderChange={onColumnOrderChange}
      onColumnFiltersChange={onColumnFiltersChange}
      onPaginationChange={onPaginationChange}
      // Required config for server-side ops
      paginateExpandedRows
      manualPagination
      manualFiltering
      rowCount={state.paginationInfo?.totalCount}
      getRowId={getTableRowId()}
      getSortedRowModel={undefined}
    >
      {children}
    </DataTable.Provider>
  );
};

export const useDirectoryTabularContext = (): DirectoryTabularContextType => {
  const context = useContext(DirectoryTabularContext);

  if (!context) {
    throw new Error("useDirectoryTabularContext must be used within DirectoryTabularProvider");
  }

  return context;
};

export const useOptionalDirectoryTabularContext = (): DirectoryTabularContextType | undefined => {
  return useContext(DirectoryTabularContext);
};

export default DirectoryTabularProvider;
